import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';

export function orgUnitKey(u: EpcProjectOrgUnit): string {
  const r = u as { _id?: string; id?: string };
  return String(r.id ?? r._id ?? '');
}

export function taskOrgUnitId(t: EpcScheduleTaskRow): string | undefined {
  const v = t.epcOrgUnitId;
  if (v == null || v === '') return undefined;
  return String(v);
}

export function taskAssignedUserId(t: EpcScheduleTaskRow): string | undefined {
  const v = t.assignedUserId;
  if (v == null || v === '') return undefined;
  return String(v);
}

export function orgUnitName(orgUnits: EpcProjectOrgUnit[], orgId: string | undefined): string {
  if (!orgId) return '';
  const u = orgUnits.find((o) => orgUnitKey(o) === orgId);
  return u?.name?.trim() || '';
}

export function membershipDisplayName(memberships: EpcProjectMembershipRow[], userId: string | undefined): string {
  if (!userId) return '';
  const row = memberships.find((m) => m.userId === userId);
  return (row?.userName || row?.userEmail || '').trim() || userId;
}

export function departmentHeadForOrg(
  memberships: EpcProjectMembershipRow[],
  orgId: string | undefined
): EpcProjectMembershipRow | undefined {
  if (!orgId) return undefined;
  return memberships.find(
    (m) => m.role === 'department_head' && m.orgUnitId === orgId && !m.inactive
  );
}

/** Managers and site engineers in this org — typical assignees for work packages under the department head. */
export function assignableMembersForOrg(
  memberships: EpcProjectMembershipRow[],
  orgId: string | undefined
): EpcProjectMembershipRow[] {
  if (!orgId) return [];
  return memberships.filter(
    (m) =>
      m.orgUnitId === orgId &&
      !m.inactive &&
      (m.role === 'manager' || m.role === 'site_engineer')
  );
}

/** Batch-set `epcOrgUnitId` for row indices; clears `assignedUserId` when it no longer fits the new org. */
export function applyOrgUnitToTaskIndices(
  tasks: EpcScheduleTaskRow[],
  indices: Iterable<number>,
  orgId: string | undefined,
  memberships: EpcProjectMembershipRow[]
): EpcScheduleTaskRow[] {
  const set = new Set(indices);
  return tasks.map((t, i) => {
    if (!set.has(i)) return t;
    if (!orgId) {
      return { ...t, epcOrgUnitId: undefined, assignedUserId: undefined };
    }
    const patch: Partial<EpcScheduleTaskRow> = { epcOrgUnitId: orgId };
    const cur = taskAssignedUserId(t);
    const allowed = new Set(assignableMembersForOrg(memberships, orgId).map((m) => m.userId));
    if (cur && !allowed.has(cur)) patch.assignedUserId = undefined;
    return { ...t, ...patch };
  });
}
